import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { postAnswer } from "../../api/answer";
import { getQuizById, getQuizIdsByScenario } from "../../api/quiz";
import { getTexts } from "../../api/text";
import handGood from "../../assets/hand-good.png";
import handWrong from "../../assets/hand-wrong.png";
import hand from "../../assets/mano.png";
import ScenarioBar from "../../components/ScenarioBar";
import { useTimer } from "../../hooks/useTimer";
import { updateScore, updateTime } from "../../redux/actions";
import { useLang } from "../../translations/i18n";
import { QUIZZES, ROUTES, SCENARIOS, SCORE_TYPES } from "../../utils/constants";
import {
	getScenarioByScreen,
	getTimer,
	updateStats,
} from "../../utils/helpers";
import { modalConfirmPrev, modalGeneric, modalGenericSmall } from "../../utils/logger";

const QUIZ_ID = QUIZZES.SCENARIO5_3;
const SCENARIO_ID = SCENARIOS.SCENARIO_5;
const NAVIGATE_TO = ROUTES.SCENARIO5_PART2_END;

const CORRECT_ANSWER_POINTS = 10;
const FIRST_ATTEMPT_EXTRA_POINTS = 20;
const WRONG_ANSWER_POINTS = -10;

function shuffleArray(arr) {
	const a = [...arr];
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
}

const DEFAULT_OPTIONS = [
	{ id: 1, text: "Voclosporina es el primer fármaco oral aprobado específicamente para el tratamiento de la nefritis lúpica en Europa.", selected: false, correct: false },
	{ id: 2, text: "Voclosporina se administra a dosis fijas y no requiere monitorización de las concentraciones plasmáticas.", selected: false, correct: false },
	{ id: 3, text: "Voclosporina se puede administrar de forma concomitante con el ácido micofenólico.", selected: false, correct: false },
	{ id: 4, text: "Voclosporina tiene un efecto favorable sobre el metabolismo de los lípidos y los electrolitos, y negativo sobre el metabolismo de la glucosa.", selected: false, correct: true },
	{ id: 5, text: "El perfil de seguridad de voclosporina es comparable al del tratamiento estándar.", selected: false, correct: false },
];

function Scenario5Part2Question2({ intl }) {
	const lang = useLang();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const participant = useSelector((state) => state.group);

	const scenarioNum = getScenarioByScreen(location.pathname);

	const [intentos, setIntentos] = useState(1);
	const [isCompleted, setIsCompleted] = useState(false);
	const [points, setPoints] = useState({ score: 0, bonus: 0, penalty: 0 });
	const [time, setTime] = useState(0);
	const [options, setOptions] = useState([]);
	const [instruction, setInstruction] = useState("");
	const [citation, setCitation] = useState("");
	const [modalCorrectTitle, setModalCorrectTitle] = useState("");
	const [modalCorrectContent, setModalCorrectContent] = useState("");
	const [resolvedQuizId, setResolvedQuizId] = useState(null);

	useEffect(() => {
		const gameId = participant?.game;
		if (!gameId) return;

		getTexts({ game: gameId })
			.then((res) => {
				if (res.status === 200 && Array.isArray(res.data)) {
					const map = res.data.reduce((acc, t) => ({ ...acc, [t.key]: t.value }), {});

					if (map["scenario5.2.2.instruction"] != null) setInstruction(map["scenario5.2.2.instruction"]);
					if (map["scenario5.2.2.citation"] != null) setCitation(map["scenario5.2.2.citation"]);
					if (map["scenario5.2.2.modalCorrect.title"] != null) setModalCorrectTitle(map["scenario5.2.2.modalCorrect.title"]);
					if (map["scenario5.2.2.modalCorrect.content"] != null) setModalCorrectContent(map["scenario5.2.2.modalCorrect.content"]);
				}
			})
			.catch(() => {});
	}, [participant?.game]);

	useEffect(() => {
		const gameId = participant?.game;
		if (!gameId) return;
		let cancelled = false;
		const params = { game: gameId };
		getQuizIdsByScenario(SCENARIO_ID, params)
			.then((listRes) => {
				if (cancelled) return;
				const list = listRes?.data && Array.isArray(listRes.data) ? listRes.data : [];
				const id = list[2]?._id || QUIZ_ID;
				setResolvedQuizId(id);
				return getQuizById(id);
			})
			.then((res) => {
				if (cancelled) return;
				if (res?.status !== 200 || !res.data?.options?.length) {
					setOptions(shuffleArray(DEFAULT_OPTIONS.map((o) => ({ ...o, selected: false }))));
					return;
				}
				const mapped = res.data.options.map((o) => ({
					id: o.id,
					text: o.text || "",
					selected: false,
					correct: Boolean(o.correct),
					explanation: o.explanation || "",
					confirmExplanation: o.confirmExplanation || "",
				}));
				setOptions(shuffleArray(mapped));
			})
			.catch(() => {
				if (!cancelled) setOptions(shuffleArray(DEFAULT_OPTIONS.map((o) => ({ ...o, selected: false }))));
			});
		return () => { cancelled = true; };
	}, [participant?.game]);

	useTimer(
		() => {
			setTime(time + 1);
		},
		isCompleted ? null : 1000
	);

	const SelectOption = ({ id, number }) => {
		let option = options.find((x) => x.id === id);

		function handleOptionClick() {
			if (isCompleted) return;

			let updatedOptions = options.map((x) =>
				x.id === id
					? { ...x, selected: !x?.selected }
					: { ...x, selected: false }
			);

			setOptions(updatedOptions);
		}

		function getBackgroundColor() {
			return option?.selected && option?.correct && isCompleted
				? "#D6D431"
				: option?.selected && !option?.correct && isCompleted
				? "#FF5948"
				: option?.selected
				? "#EAF886"
				: "#FFFFFF";
		}

		return (
			<div>
				<div
					className={`d-flex align-items-center justify-content-start txt-dark-green p-3 mb-3 ${
						option?.selected ? "option-selected bold" : ""
					}`}
					onClick={handleOptionClick}
					style={{
						position: "relative",
						minHeight: "10vh",
						height: "fit-content",
						borderRadius: "10px",
						lineHeight: "20px",
						backgroundColor: getBackgroundColor(),
					}}
				>
					<div className="d-flex align-items-start">
						<div>{number}.</div>
						<div className="ms-2" dangerouslySetInnerHTML={{ __html: option?.text || "" }} />
					</div>
				</div>
			</div>
		);
	};

	function checkAnswers() {
		const selectedOption = options?.find((x) => x.selected);
		const correct = selectedOption?.correct;

		const score = correct
			? CORRECT_ANSWER_POINTS +
			  (intentos === 1 ? FIRST_ATTEMPT_EXTRA_POINTS : 0)
			: WRONG_ANSWER_POINTS;

		setPoints({
			...points,
			score: points.score + score,
			bonus: correct && intentos === 1 ? FIRST_ATTEMPT_EXTRA_POINTS : 0,
			penalty: correct
				? points.penalty
				: points.penalty + WRONG_ANSWER_POINTS,
		});

		if (correct) {
			// Justificada: título + puntos + justificación (explanation) del quiz o texto de Texts
			const bodyContent = (selectedOption?.explanation && selectedOption.explanation.trim() !== "")
				? selectedOption.explanation
				: (modalCorrectContent ?? "");
			const html = `
			  <div class="txt14">
				<div class="txt-turquoise bold txt18 text-center">
				  Justificada
				</div>
				<div class="text-center mt-1 txt-black">${modalCorrectTitle ?? ""}</div>
		  
				<div class="d-flex justify-content-center my-3 text-center">
				  <div class="bg-correct txt-black w-fit-content p-2 bold">
					+${score} puntos
				  </div>
				</div>
		  
				<div class="text-left mt-3">${bodyContent}</div>
			  </div>
			`;
		  
			modalGeneric({ html });
			setIsCompleted(true);
		} else {
			// No justificada: resultado + puntos + justificación (explanation) de la opción elegida
			const justificationBlock = (selectedOption?.explanation && selectedOption.explanation.trim() !== "")
				? `<div class="mt-3 text-left">${selectedOption.explanation}</div>`
				: "";
			const text = `<div class="txt14">
					<div class="text-center">
						<div class="txt-turquoise bold txt18">No justificada</div>
						<div class="txt-black mt-1">Esta no es la afirmación infiltrada.</div>
					</div>
					<div class="d-flex justify-content-center my-3">
						<div class="bg-incorrect txt-black w-fit-content p-2">
							<img width="20" src=${handWrong} class="me-1" />
							${score} puntos
						</div>
					</div>
					${justificationBlock}
					<div class="mt-2 text-center">Inténtalo de nuevo.</div>					
				</div>`;

			modalGenericSmall({ html: text, allowOutsideClick: true });
		}

		setIntentos(intentos + 1);
	}

	async function submitAnswer() {
		let totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));
		postAnswer({
			scenario: SCENARIO_ID,
			quiz: resolvedQuizId ?? QUIZ_ID,
			group: participant?._id,
			score: points.score,
			time: time,
			correct: true,
		})
			.then((res) => {
				if (res.status === 201) {
					dispatch(updateScore(points.score, SCORE_TYPES.SCORE));
					dispatch(updateScore(points.bonus, SCORE_TYPES.BONUS));
					dispatch(updateScore(points.penalty, SCORE_TYPES.PENALTY));

					updateStats({
						...participant,
						time: totalTime,
						score: participant?.score + points.score,
						bonus: participant?.bonus + points.bonus,
						penaltyScore:
							participant?.penaltyScore + points.penalty,
						lastVisited: NAVIGATE_TO,
					});
					navigate(NAVIGATE_TO, {
						state: { activeScenario: scenarioNum + 1 },
					});
				}
			})
			.catch((error) => {});
	}

	return (
		<div className="h-100 bg-prueba">
			<div className="container">
				<div className="row h-100">
					<div className="d-flex justify-content-start align-items-center mb-1">
						<img className="me-2" src={hand} />
						<div
						className="txt-white"
						dangerouslySetInnerHTML={{ __html: instruction || "" }}
						/>

					</div>
					<div className="row d-flex justify-content-center align-items-start">
					<div
  className="col-4 txt-white"
  style={{
    fontSize: "12px",
    lineHeight: "15px",
  }}
  dangerouslySetInnerHTML={{ __html: citation }}
/>




						<div className="col-8">
							{options.length === 0 ? (
								<div className="txt-white">Cargando opciones...</div>
							) : (
								options.map((option, index) => (
									<div key={option.id}>
										<SelectOption id={option.id} number={index + 1} />
									</div>
								))
							)}
						</div>
					</div>
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					right: "0",
					bottom: "13vh",
					zIndex: 1000,
				}}
			>
				<div className="d-flex justify-content-end">
					<div
						className="btn-main"
						onClick={() => {
							if (isCompleted) {
								submitAnswer();
								return;
							}
							const selectedOption = options?.find((x) => x.selected);
							// Confirmación previa: si la opción tiene contenido, se muestra antes de comprobar
							if (selectedOption?.confirmExplanation && selectedOption.confirmExplanation.trim() !== "") {
								modalConfirmPrev({ html: selectedOption.confirmExplanation }).then((res) => {
									if (res?.isConfirmed) checkAnswers();
								});
							} else {
								checkAnswers();
							}
						}}
					>
						<div className="text-btn-main">AVANZAR</div>
					</div>
				</div>
			</div>
			<ScenarioBar activeScenario={scenarioNum}></ScenarioBar>
		</div>
	);
}

export default injectIntl(Scenario5Part2Question2);
